import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Sparkles, X, CheckCircle2, Rocket, MapPin, Zap, ArrowRight, ArrowLeft } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const httpAuth = () => ({ Authorization: `Bearer ${localStorage.getItem("wt_token")}` });

const LEAD_SOURCE_KINDS = [
  { kind: "webform", label: "Website form", hint: "e.g. Contact-us or quote form on your site" },
  { kind: "whatsapp", label: "WhatsApp business", hint: "Enquiries via your WhatsApp Business number" },
  { kind: "email", label: "Shared inbox", hint: "hello@ or sales@ inbox for enquiries" },
  { kind: "phone", label: "Phone calls", hint: "Direct phone / missed-call bookings" },
  { kind: "csv", label: "CSV upload", hint: "Import an existing lead list from a spreadsheet" },
  { kind: "crm", label: "External CRM", hint: "Sync from HubSpot / Zoho / Pipedrive" },
];

/**
 * OnboardingWizard — full-screen modal that appears on /my until completed
 * or dismissed. Two guided steps: connect Google Business Profile, add a
 * lead source. Finishing the wizard seeds a lightweight baseline trend so
 * the sparklines light up on day one.
 */
export default function OnboardingWizard({ onDone }) {
  const [state, setState] = useState(null);   // {status, completed, dismissed, steps}
  const [step, setStep] = useState(0);        // 0=welcome, 1=gbp, 2=lead source, 3=review
  const [saving, setSaving] = useState(false);

  // Step 1 (GBP) form
  const [gbp, setGbp] = useState({
    business_name: "", profile_url: "", place_id: "",
    primary_city: "", primary_category: "",
  });
  // Step 2 (Lead Source) form
  const [ls, setLs] = useState({ kind: "webform", label: "", detail: "", monthly_volume: 10 });

  useEffect(() => {
    axios.get(`${API}/my/onboarding`, { headers: httpAuth() })
      .then((r) => {
        setState(r.data);
        if (r.data?.steps?.gbp) setGbp((g) => ({ ...g, ...r.data.steps.gbp }));
        if (r.data?.steps?.lead_source) setLs((s) => ({ ...s, ...r.data.steps.lead_source }));
      })
      .catch(() => setState({ status: "pending", completed: false, steps: {} }));
  }, []);

  if (!state) return null;
  // Hide once completed or dismissed.
  if (state.completed || state.dismissed) return null;

  const closeAndDismiss = async () => {
    if (!window.confirm("Skip onboarding? You can restart it anytime from the dashboard.")) return;
    try {
      await axios.post(`${API}/my/onboarding/dismiss`, {}, { headers: httpAuth() });
      onDone?.({ dismissed: true });
    } catch { toast.error("Could not dismiss"); }
  };

  const saveGbp = async () => {
    if (!gbp.business_name?.trim()) return toast.error("Business name is required");
    setSaving(true);
    try {
      const r = await axios.post(`${API}/my/onboarding/step`,
        { step: "gbp", gbp }, { headers: httpAuth() });
      setState(r.data);
      setStep(2);
      toast.success("Google Business Profile connected");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const saveLs = async () => {
    if (!ls.kind) return toast.error("Choose a lead source");
    setSaving(true);
    try {
      const r = await axios.post(`${API}/my/onboarding/step`,
        { step: "lead_source", lead_source: ls }, { headers: httpAuth() });
      setState(r.data);
      setStep(3);
      toast.success("Lead source saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const complete = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/my/onboarding/complete`,
        { seed_baseline: true }, { headers: httpAuth() });
      const seeded = r.data?.seeded_leads || 0;
      toast.success(seeded > 0
        ? `You're set. Seeded ${seeded} baseline leads so your trend chart lights up.`
        : "You're all set!");
      onDone?.({ completed: true, seeded });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not complete"); }
    finally { setSaving(false); }
  };

  const StepDot = ({ n, active, done }) => (
    <div className={`flex items-center gap-2`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ring-2 ${
          done ? "bg-emerald-600 text-white ring-emerald-200"
               : active ? "bg-slate-900 text-white ring-slate-200"
                        : "bg-slate-100 text-slate-500 ring-slate-100"}`}>
        {done ? <CheckCircle2 className="w-4 h-4" /> : n}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="onboarding-wizard">
      <Card className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-gradient-to-r from-emerald-50 via-white to-sky-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center">
              <Rocket className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">One-time setup</div>
              <div className="text-lg font-bold text-slate-900">Light up your dashboard</div>
            </div>
          </div>
          <button onClick={closeAndDismiss}
                  className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
                  data-testid="onboarding-skip-btn"
                  aria-label="Skip onboarding">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-6 pt-5 flex items-center gap-3">
          <StepDot n="1" active={step === 1} done={step > 1 || !!state.steps?.gbp} />
          <div className={`flex-1 h-px ${step > 1 || state.steps?.gbp ? "bg-emerald-400" : "bg-slate-200"}`} />
          <StepDot n="2" active={step === 2} done={step > 2 || !!state.steps?.lead_source} />
          <div className={`flex-1 h-px ${step > 2 ? "bg-emerald-400" : "bg-slate-200"}`} />
          <StepDot n="3" active={step === 3} done={false} />
        </div>

        {/* Step content */}
        <div className="p-6" data-testid={`onboarding-step-${step}`}>
          {step === 0 && (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div>
                  <div className="font-bold text-slate-900">
                    Two quick steps and your Growth Story starts today.
                  </div>
                  <p className="text-sm text-slate-600 mt-1">
                    Connect your Google Business Profile so we can pull local
                    visibility signals, then tell us how leads reach you — we'll
                    seed a baseline trend so day-one is never blank.
                  </p>
                </div>
              </div>
              <ul className="text-sm text-slate-700 space-y-2 pl-1">
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                  <span><b>Step 1:</b> Google Business Profile — search visibility + reviews</span>
                </li>
                <li className="flex items-start gap-2">
                  <Zap className="w-4 h-4 text-slate-400 mt-0.5" />
                  <span><b>Step 2:</b> A lead source — where enquiries come in today</span>
                </li>
              </ul>
              <div className="pt-3 flex justify-end">
                <Button onClick={() => setStep(1)} className="bg-slate-900 hover:bg-slate-800 rounded-full"
                        data-testid="onboarding-start-btn">
                  Let's go <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              <div className="text-sm text-slate-600">
                <b className="text-slate-900">Connect your Google Business Profile</b> — paste your public listing link or the Place ID.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-xs text-slate-500 font-semibold">
                  Business name*
                  <input value={gbp.business_name}
                          onChange={(e) => setGbp({ ...gbp, business_name: e.target.value })}
                          placeholder="Acme Clinic"
                          className="mt-1 w-full text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 font-normal text-slate-900"
                          data-testid="onboarding-gbp-name" />
                </label>
                <label className="text-xs text-slate-500 font-semibold">
                  Primary city
                  <input value={gbp.primary_city}
                          onChange={(e) => setGbp({ ...gbp, primary_city: e.target.value })}
                          placeholder="Austin, TX"
                          className="mt-1 w-full text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 font-normal text-slate-900"
                          data-testid="onboarding-gbp-city" />
                </label>
                <label className="text-xs text-slate-500 font-semibold sm:col-span-2">
                  Google Business Profile URL
                  <input value={gbp.profile_url}
                          onChange={(e) => setGbp({ ...gbp, profile_url: e.target.value })}
                          placeholder="https://g.page/acme-clinic or https://www.google.com/maps/…"
                          className="mt-1 w-full text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 font-normal text-slate-900"
                          data-testid="onboarding-gbp-url" />
                </label>
                <label className="text-xs text-slate-500 font-semibold">
                  Place ID (optional)
                  <input value={gbp.place_id}
                          onChange={(e) => setGbp({ ...gbp, place_id: e.target.value })}
                          placeholder="ChIJ…"
                          className="mt-1 w-full text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 font-normal text-slate-900"
                          data-testid="onboarding-gbp-placeid" />
                </label>
                <label className="text-xs text-slate-500 font-semibold">
                  Primary category
                  <input value={gbp.primary_category}
                          onChange={(e) => setGbp({ ...gbp, primary_category: e.target.value })}
                          placeholder="Dental clinic"
                          className="mt-1 w-full text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 font-normal text-slate-900"
                          data-testid="onboarding-gbp-category" />
                </label>
              </div>
              <p className="text-xs text-slate-500">
                We don't touch your Google account yet — this saves the record so
                our team can OAuth-connect on your behalf.
              </p>
              <div className="pt-2 flex justify-between">
                <Button variant="outline" onClick={() => setStep(0)} className="rounded-full">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button onClick={saveGbp} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-full"
                        data-testid="onboarding-save-gbp">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Save & continue <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div className="text-sm text-slate-600">
                <b className="text-slate-900">How do enquiries reach you today?</b> Pick the strongest source — you can add more later.
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {LEAD_SOURCE_KINDS.map((k) => (
                  <button key={k.kind}
                          type="button"
                          onClick={() => setLs({ ...ls, kind: k.kind, label: k.label })}
                          className={`text-left px-3 py-2.5 rounded-lg ring-1 ${
                            ls.kind === k.kind
                              ? "ring-2 ring-slate-900 bg-slate-900 text-white"
                              : "ring-slate-200 bg-white hover:bg-slate-50 text-slate-800"}`}
                          data-testid={`onboarding-source-${k.kind}`}>
                    <div className="text-sm font-semibold">{k.label}</div>
                    <div className={`text-[11px] mt-0.5 ${ls.kind === k.kind ? "text-slate-300" : "text-slate-500"}`}>
                      {k.hint}
                    </div>
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-xs text-slate-500 font-semibold">
                  Where should we look?
                  <input value={ls.detail}
                          onChange={(e) => setLs({ ...ls, detail: e.target.value })}
                          placeholder={ls.kind === "webform" ? "https://yoursite.com/contact"
                                        : ls.kind === "whatsapp" ? "+1 555 000 1234"
                                        : ls.kind === "email" ? "hello@yourbusiness.com"
                                        : "Details"}
                          className="mt-1 w-full text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 font-normal text-slate-900"
                          data-testid="onboarding-source-detail" />
                </label>
                <label className="text-xs text-slate-500 font-semibold">
                  Rough monthly volume
                  <input type="number" value={ls.monthly_volume}
                          onChange={(e) => setLs({ ...ls, monthly_volume: parseInt(e.target.value || "0", 10) })}
                          min={0} max={2000}
                          className="mt-1 w-full text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 font-normal text-slate-900"
                          data-testid="onboarding-source-volume" />
                </label>
              </div>
              <p className="text-xs text-slate-500">
                We'll seed a light baseline trend from this so your chart isn't blank on day one. Real inbound leads always take priority.
              </p>
              <div className="pt-2 flex justify-between">
                <Button variant="outline" onClick={() => setStep(1)} className="rounded-full">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button onClick={saveLs} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-full"
                        data-testid="onboarding-save-source">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Save & continue <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3" data-testid="onboarding-review">
              <div className="text-sm text-slate-600">
                <b className="text-slate-900">Almost done.</b> Confirm your setup — we'll seed a baseline so your chart lights up today.
              </div>
              <div className="rounded-xl bg-slate-50 ring-1 ring-slate-100 p-4 text-sm space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Google Business</div>
                    <div className="font-medium text-slate-900">{gbp.business_name || "—"}</div>
                    {gbp.primary_city && <div className="text-xs text-slate-500">{gbp.primary_city} · {gbp.primary_category || "—"}</div>}
                  </div>
                  <button onClick={() => setStep(1)} className="text-xs text-sky-600 hover:underline">Edit</button>
                </div>
                <div className="border-t border-slate-100 pt-2 flex items-start justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Lead Source</div>
                    <div className="font-medium text-slate-900">{ls.label || ls.kind}</div>
                    <div className="text-xs text-slate-500">
                      {ls.detail || "—"} · ~{ls.monthly_volume || 0} leads/month
                    </div>
                  </div>
                  <button onClick={() => setStep(2)} className="text-xs text-sky-600 hover:underline">Edit</button>
                </div>
              </div>
              <div className="pt-2 flex justify-between">
                <Button variant="outline" onClick={() => setStep(2)} className="rounded-full">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button onClick={complete} disabled={saving}
                        className="bg-emerald-600 hover:bg-emerald-700 rounded-full text-white"
                        data-testid="onboarding-complete-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Finish & light up my chart <Sparkles className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
